import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import CircleArrowRightIcon from "../../Components/Icons/CircleArrowRightIcon";
import {
    BandLabel,
    FieldLabel,
    INCOMING_BAND_COLOR,
    INCOMING_BAND_SX,
    OUTGOING_BAND_COLOR,
    OUTGOING_BAND_SX,
    PackCardShell,
} from "../RecordReplacement/form/PackCardShared";
import SerialSearchField from "../RecordReplacement/form/SerialSearchField";
import {
    MIN_SERIAL_SEARCH_LENGTH,
    SERIAL_LOOKUP_DEBOUNCE_MS,
    getSerialFieldMessage,
    isRegistryDateLocked,
    matchesAutoFilledSerial,
    toCalendarDay,
} from "../RecordReplacement/recordReplacementHelper";
import {
    getBatteryPackBySerial,
    getPackSerialSuggestions,
} from "../../Api/BatteryPackApi";
import ScreenContainer from "../../Components/ScreenContainer";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../Features/Table/GenericDraggableTable/TableStyles";
import { ChangedChip } from "../../Components/EditVehicleDetails/EditDialogShared";
import RemarksSection from "../RecordReplacement/form/RemarksSection";
import SingleDatePicker from "../../Helper/DatePicker/SingleDatePicker";
import CartLoader from "../../Helper/Loaders/CartLoader";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { useToast } from "../../Hooks/useToast";
import {
    decryptTheParams,
    encryptTheParams,
} from "../../Helper/QueryParams/EncryptDecrypt";
import {
    convertZonetoUTCStart,
    getDatePickerDate,
    getLocalDateFromUTC,
} from "../../Helper/DatePicker/DateConverter";
import { DateFormatString } from "../../Helper/DatePicker/DateFormatters";
import { transformTwinHierarchyData } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractStringsWithPacks } from "../../Helper/DigitalTwin/BatteryComponentsHelper";
import {
    MAX_PACK_REMARK_LENGTH,
    buildBulkPackDetailsPayload,
    applyServerFieldErrors,
    buildInitialPackForm,
    formatSerialSlotConflict,
    getSerialSlotConflict,
    byPack,
    fieldRefKey,
    firstErrorField,
    buildReplacementWarning,
    hasBlockingErrors,
    hasPackDetailsChanged,
    validatePackDetailsForm,
} from "../../Helper/DigitalTwin/editPackDetailsHelper";
import {
    getBatteryComponents,
    updatePackDetails,
} from "../../Api/ServiceRecordApi";

// Edit Pack Details — a full screen, reached from the Battery grid's pencil (one pack)
// or from selection mode (several).
//
// A screen rather than a dialog, matching Record Replacement: both entry points open
// the page in a new tab, so a correction reads as the same kind of act as a
// replacement and there is exactly ONE edit UI to maintain.
//
// Packs are re-resolved from the components tree rather than carried in the URL. Params
// flatten to strings so only the slot numbers can travel, and re-resolving keeps the
// values fresh -- a serial captured at selection time may already be stale by submit,
// which is precisely what the backend's `expected` guard should catch.
export default function EditPackDetailsPage() {
    const navigate = useNavigate();
    const { t } = useTranslation(["digital_twin", "common"]);
    const { openNotification } = useToast();
    const { hasPermission, isRbacV2Enabled } = usePermissions();

    const params = useMemo(() => decryptTheParams(), []);
    const { batteryID, absPackNos, vehicleNumber, pageType } = params || {};

    const requestedAbsPackNos = useMemo(
        () =>
            String(absPackNos || "")
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean),
        [absPackNos]
    );

    // "update", matching the endpoint this page puts to and the gate on the controls
    // that lead here. Asking for a different verb than the buttons do is what once
    // showed users a Replace control that 403'd on submit.
    const canEditDetails = !isRbacV2Enabled()
        ? true
        : hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, "update");

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [packs, setPacks] = useState([]);
    const [forms, setForms] = useState([]);
    const [remark, setRemark] = useState("");
    const [errors, setErrors] = useState({ rows: {} });
    const [submitting, setSubmitting] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [missingAbsPackNos, setMissingAbsPackNos] = useState([]);
    const [allPacks, setAllPacks] = useState([]);

    const toLocalDay = useCallback(
        (value) => getLocalDateFromUTC(value, "YYYY-MM-DD"),
        []
    );

    // Direct-URL guard: without the permission there is nothing to do here.
    useEffect(() => {
        if (!canEditDetails) navigate("/inventory", { replace: true });
    }, [canEditDetails, navigate]);

    useEffect(() => {
        // Skip the fetch entirely when the guard above is redirecting; otherwise a
        // user without the permission issues a GET on their way out.
        if (!canEditDetails) return undefined;
        if (!batteryID || !requestedAbsPackNos.length) {
            setLoading(false);
            setLoadError(true);
            return undefined;
        }
        let active = true;
        (async () => {
            try {
                const res = await getBatteryComponents(batteryID);
                if (!active) return;
                const tree = transformTwinHierarchyData(
                    res?.response?.BATTERY_SYSTEM || [],
                    t
                );
                // Flatten to the requested slots, in SLOT order rather than the order
                // the user happened to tick them, so the cards read down the bus.
                const wanted = new Set(requestedAbsPackNos.map(String));
                const resolved = [];
                // Every pack on the bus, in slot order -- the pool the "Add another
                // pack" menu draws from. Held separately from `packs`, which is only
                // the ones currently being corrected.
                const everyPack = [];
                extractStringsWithPacks(tree).forEach((string) => {
                    (string?.packs || []).forEach((pack) => {
                        const withLabel = { ...pack, stringLabel: string?.label || "" };
                        everyPack.push(withLabel);
                        if (wanted.has(String(pack?.absPackNo))) resolved.push(withLabel);
                    });
                });
                setAllPacks(everyPack);

                setPacks(resolved);
                setForms(resolved.map((pack) => buildInitialPackForm(pack, toLocalDay)));
                setLoadError(!resolved.length);
                // A slot can disappear or renumber between selection and load. Without
                // this the page just renders fewer cards, and the user confirms
                // believing every pack they picked was corrected.
                const found = new Set(resolved.map((pack) => String(pack.absPackNo)));
                setMissingAbsPackNos(
                    requestedAbsPackNos.filter((no) => !found.has(String(no)))
                );
            } catch (err) {
                console.error("Error loading packs to edit:", err);
                if (active) setLoadError(true);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [batteryID, canEditDetails, requestedAbsPackNos, t, toLocalDay]);

    const initials = useMemo(
        () => packs.map((pack) => buildInitialPackForm(pack, toLocalDay)),
        [packs, toLocalDay]
    );

    // Every read that pairs a pack with its form or its starting values goes through
    // these, never through a shared array index -- see byPack.
    const formByPack = useMemo(() => byPack(forms), [forms]);
    const initialByPack = useMemo(() => byPack(initials), [initials]);

    const isMulti = packs.length > 1;

    // Every slot this request covers. A serial sitting in one of them is NOT a clash:
    // the request frees it in the same transaction, which is what makes correcting two
    // transposed serials a single valid submit. Same exclusion the backend applies.
    const editedAbsPackNos = useMemo(
        () => packs.map((pack) => pack.absPackNo),
        [packs]
    );

    // Editing a pack that has a recorded replacement behind it is legitimate -- a
    // serial mistyped while recording that replacement is exactly what this fixes --
    // but it is the one case where a correction can disagree with history, so say so.
    const replacementWarning = useMemo(
        () =>
            buildReplacementWarning(packs, (value) =>
                DateFormatString(getLocalDateFromUTC(value, "YYYY-MM-DD"))
            ),
        [packs]
    );

    // Packs on this bus not already being corrected, in slot order. Only ones that are
    // actually addressable -- a node with no absPackNo has nothing to PUT to.
    const availablePacks = useMemo(() => {
        const chosen = new Set(packs.map((pack) => String(pack.absPackNo)));
        return allPacks.filter(
            (pack) => pack?.absPackNo != null && !chosen.has(String(pack.absPackNo))
        );
    }, [allPacks, packs]);

    // Added packs land in SLOT order, not append order, so the cards keep reading down
    // the bus however the user assembled the list -- same reasoning as the selection
    // hand-off, which sorts rather than preserving tick order.
    const addPack = (pack) => {
        setPacks((prev) => [...prev, pack].sort((a, b) => a.absPackNo - b.absPackNo));
        setForms((prev) =>
            [...prev, buildInitialPackForm(pack, toLocalDay)].sort(
                (a, b) => a.absPackNo - b.absPackNo
            )
        );
        setErrors((prev) => ({ ...prev, rows: {}, submit: undefined }));
    };

    const removePack = (pack) => {
        setPacks((prev) => prev.filter((p) => p.absPackNo !== pack.absPackNo));
        setForms((prev) => prev.filter((f) => f.absPackNo !== pack.absPackNo));
        setErrors((prev) => ({ ...prev, rows: {}, submit: undefined }));
    };

    // Keyed on absPackNo, NOT on the card's position. addPack re-sorts both arrays into
    // slot order and removePack filters them, so a position captured when a card
    // rendered can name a different pack -- or no pack -- by the time an in-flight
    // registry lookup resolves and writes through it. The Replace flow keys its own
    // mutator on pack identity for the same reason.
    const setField = (absPackNo, field) => (value) => {
        setForms((prev) =>
            prev.map((row) =>
                row.absPackNo === absPackNo ? { ...row, [field]: value } : row
            )
        );
        setErrors((prev) => ({ ...prev, rows: {}, submit: undefined }));
    };

    // This screen opens in its OWN browser tab (encryptTheParams(..., openInNewTab)),
    // so its history has exactly one entry and navigate(-1) silently does nothing --
    // which left Cancel, the back link and the post-submit exit all dead. Same pair
    // RecordReplacementPage uses: close the tab on cancel, and on success navigate to
    // the battery's Components tab so the user sees the corrected packs.
    const closeTab = () => {
        // window.close() only works on a tab the script opened. Both entry points do
        // open one, but this URL is a self-contained shareable link -- pasted,
        // bookmarked or reopened from history, the close is refused and the control
        // does nothing. That is the same dead-control bug as navigate(-1), just on a
        // different path, so fall through to a real navigation.
        window.close();
        if (!window.closed) {
            if (batteryID) goToComponents();
            else navigate("/inventory", { replace: true });
        }
    };

    const goToComponents = () =>
        encryptTheParams(
            { batteryID, tabValue: "components", pageType: pageType || "" },
            navigate,
            true
        );

    // Anything the user typed is worth confirming before discarding: a multi-pack
    // correction plus up to 1000 characters of justification, with no draft and no
    // undo. Record Replacement, which this page mirrors, confirms for the same reason.
    const hasUnsavedWork =
        remark.trim().length > 0 ||
        forms.some((form) => hasPackDetailsChanged(form, initialByPack[form?.absPackNo]));

    // One flat map of every focusable field on the page, keyed by fieldRefKey, filled
    // by the cards and the remark as they render. `revealField` is a one-shot: set on a
    // failed submit, consumed and cleared by the effect below.
    const fieldRefs = useRef({});
    const [revealField, setRevealField] = useState(null);

    // Runs after the render that produced the node, so there is something to scroll to.
    //
    // The registered node is the field's CONTAINER, not its input: the serial box, the
    // date pickers and the remark are three different components with three different
    // ways of exposing an input ref, and threading one through DeviceSearchInput would
    // change a component shared with the inventory tables. Scroll the container, then
    // focus whatever input it holds.
    useEffect(() => {
        if (!revealField) return;
        const node = fieldRefs.current[revealField];
        setRevealField(null);
        if (!node) return;

        node.scrollIntoView({ behavior: "smooth", block: "center" });
        // A disabled input takes no focus, and the registry-locked MFG date is exactly
        // that -- a server conflict can name it. Scrolling to it is still right.
        const input = node.querySelector("input:not([disabled]), textarea:not([disabled])");
        if (!input) return;

        // After the smooth scroll starts, so focus does not fight it by jumping the
        // page to the field first. Cleared on unmount: this page can navigate away at
        // any moment -- Cancel, Discard, or a submit that succeeded.
        const timer = window.setTimeout(() => input.focus({ preventScroll: true }), 300);
        return () => clearTimeout(timer);
    }, [revealField]);

    const registerField = useCallback((key, node) => {
        if (node) fieldRefs.current[key] = node;
        else delete fieldRefs.current[key];
    }, []);

    const requestCancel = () => {
        if (hasUnsavedWork) setCancelOpen(true);
        else closeTab();
    };

    const handleSubmit = async () => {
        const errs = validatePackDetailsForm({
            forms,
            initials,
            remark,
            packs,
            batteryID,
        });
        setErrors(errs);
        if (hasBlockingErrors(errs)) {
            // Take the user to the problem rather than describing it at the bottom of
            // a form taller than the screen. With several cards open, a summary can sit
            // off screen from every field it is about.
            setRevealField(fieldRefKey(firstErrorField(errs, packs)));
            return;
        }

        setSubmitting(true);
        try {
            const payload = buildBulkPackDetailsPayload({
                packs,
                forms,
                initials,
                remark,
                toUTC: convertZonetoUTCStart,
            });
            const response = await updatePackDetails(batteryID, payload);

            // GetDataWithErrors returns the server envelope verbatim on success AND on
            // 400/409/422, so the backend's specific message survives -- which pack,
            // which field moved and to what, or that a serial is already active
            // elsewhere.
            if (response?.responseStatus?.code !== 200) {
                const msg =
                    response?.responseStatus?.msg ||
                    "Could not update the pack details. Please try again.";
                // A conflict the backend can attribute to one pack's field -- a serial
                // already active elsewhere, a value someone else moved -- is reported
                // ON that field and scrolled to, exactly like a local validation
                // error. Only what it cannot attribute falls back to the banner.
                const errs = applyServerFieldErrors(
                    response?.response?.fieldErrors,
                    packs,
                    msg
                );
                setErrors(errs);
                setRevealField(fieldRefKey(firstErrorField(errs, packs)));
                openNotification({ text: msg, icon: "toast-error" });
                return;
            }

            openNotification({
                text: response?.responseStatus?.msg || "Pack details updated",
                icon: "toast-success",
            });
            // Navigating away also retires this form. Staying put would leave `packs`
            // holding the PRE-edit values, so a second Confirm would send an `expected`
            // block the database no longer matches and 409 on a correction the user
            // made no mistake in.
            goToComponents();
        } catch (error) {
            // GetDataWithErrors synthesises an envelope even for a 5xx or a
            // no-response write, so this only catches a synchronous throw while
            // building the payload.
            const msg =
                error?.response?.data?.responseStatus?.msg ||
                "Could not update the pack details. Please try again.";
            const errs = applyServerFieldErrors(
                error?.response?.data?.response?.fieldErrors,
                packs,
                msg
            );
            setErrors(errs);
            setRevealField(fieldRefKey(firstErrorField(errs, packs)));
            openNotification({ text: msg, icon: "toast-error" });
        } finally {
            setSubmitting(false);
        }
    };

    // ScreenContainer supplies the AppBar/SideBar offsets every full page needs.
    // Without it the page renders at viewport 0 -- under the sidebar on the left and
    // overflowing past the right edge.
    //
    // Each of the three returns below sets the 24px horizontal inset on its OWN
    // outermost box and nothing under it re-declares one -- the page layout rule in
    // CLAUDE.md. The sticky header cancels it with a negative margin so its rule spans
    // the full width, which is why it reads as -24px rather than 0.
    if (loading) {
        return (
            <ScreenContainer pagesContent={null}>
                <Box
                    sx={{
                        px: 3,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60vh",
                    }}
                >
                    <CartLoader />
                </Box>
            </ScreenContainer>
        );
    }

    if (loadError) {
        return (
            <ScreenContainer pagesContent={null}>
                <Box data-testid="edit-pack-load-error" sx={{ px: 3, pt: 4 }}>
                    <BackLink onClick={closeTab} />
                    <Typography sx={{ fontSize: "14px", color: "#6B6B6B" }}>
                        These packs could not be loaded. They may have been replaced
                        since you opened this page — go back and try again.
                    </Typography>
                </Box>
            </ScreenContainer>
        );
    }

    return (
        <ScreenContainer pagesContent={null}>
            <Box sx={{ px: "24px", pt: "14px", pb: "80px" }}>
            {/* ---- Top header (breadcrumb + title + actions) — frozen on scroll ---- */}
            <Box
                sx={{
                    position: "sticky",
                    top: "50px",
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #EAEAEA",
                    mx: "-24px",
                    px: "24px",
                    py: "16px",
                    mb: "4px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                mb: "6px",
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "13px", fontWeight: 600, color: "#8D8D8D" }}
                            >
                                FLEET
                            </Typography>
                            <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>
                                /
                            </Typography>
                            <Typography
                                sx={{ fontSize: "13px", fontWeight: 600, color: "#4A4AF4" }}
                            >
                                {vehicleNumber || batteryID}
                            </Typography>
                            {packs.length > 0 && (
                                <Box
                                    data-testid="edit-pack-count"
                                    sx={{
                                        fontSize: "12px",
                                        color: "#494949",
                                        backgroundColor: "#F0F0F0",
                                        borderRadius: "4px",
                                        padding: "2px 10px",
                                    }}
                                >
                                    {packs.length === 1
                                        ? [packs[0].stringLabel, packs[0].label]
                                              .filter(Boolean)
                                              .join(" ")
                                        : `${packs.length} packs`}
                                </Box>
                            )}
                        </Box>
                        <Typography
                            sx={{ fontSize: "26px", fontWeight: 700, color: "#1A1A1A" }}
                        >
                            Edit Pack Details
                        </Typography>
                    </Box>

                    {/* Submit sits top AND bottom, as on Record Replacement: with
                        several packs open the bottom action is far below the fold. */}
                    <Box sx={{ display: "flex", gap: "12px" }}>
                        <Button
                            data-testid="edit-pack-cancel-top"
                            disabled={submitting}
                            onClick={requestCancel}
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#494949",
                                border: "1px solid #D0D0D0",
                                borderRadius: "8px",
                                padding: "8px 22px",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                                "&.Mui-disabled": {
                                    color: "#A4A4A4",
                                    border: "1px solid #E4E4E4",
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="edit-pack-confirm-top"
                            disableElevation
                            disabled={submitting}
                            onClick={handleSubmit}
                            startIcon={
                                submitting ? (
                                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                                ) : null
                            }
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: "#6D72F6",
                                borderRadius: "8px",
                                padding: "8px 22px",
                                "&:hover": { backgroundColor: "#5A5FE0" },
                                "&.Mui-disabled": {
                                    backgroundColor: "#D8D8D8",
                                    color: "#fff",
                                },
                            }}
                        >
                            {submitting ? "Saving" : "Confirm Change"}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ---- Sub-breadcrumb ---- */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", py: "16px" }}>
                <Typography sx={{ fontSize: "15px", color: "#8D8D8D" }}>
                    Digital Twin/Component/{" "}
                    <span style={{ color: "#1A1A1A", fontWeight: 600 }}>
                        Edit Pack Details
                    </span>
                </Typography>
            </Box>

            {/* ---- Body: single outer container holding all section blocks ---- */}
            <Box
                sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    padding: "16px",
                }}
            >
                {isMulti && (
                    <Typography
                        sx={{ fontSize: "13px", color: "#6B6B6B", mb: 2 }}
                    >
                        All packs are corrected together. If any one fails, none are
                        saved.
                    </Typography>
                )}

                {missingAbsPackNos.length > 0 && (
                    <NoticeBanner
                        tone="warning"
                        testId="edit-pack-missing"
                        title={
                            missingAbsPackNos.length === 1
                                ? "One pack could not be loaded"
                                : `${missingAbsPackNos.length} packs could not be loaded`
                        }
                    >
                        Pack {missingAbsPackNos.join(", ")} is not shown below and will
                        not be corrected. It may have been replaced since you selected
                        it.
                    </NoticeBanner>
                )}

                {replacementWarning && (
                    <ReplacementWarning warning={replacementWarning} isMulti={isMulti} />
                )}

                {packs.map((pack) => (
                    <PackEditCard
                        key={pack.absPackNo}
                        pack={pack}
                        form={formByPack[pack.absPackNo]}
                        errors={errors.rows?.[pack.absPackNo] || {}}
                        changed={hasPackDetailsChanged(
                            formByPack[pack.absPackNo],
                            initialByPack[pack.absPackNo]
                        )}
                        batteryID={batteryID}
                        // Never removable down to nothing: an empty request has nothing
                        // to submit, and the page would offer a Confirm that cannot
                        // succeed.
                        canRemove={packs.length > 1}
                        onRemove={removePack}
                        onChange={(field) => setField(pack.absPackNo, field)}
                        registerField={registerField}
                        editedAbsPackNos={editedAbsPackNos}
                    />
                ))}

                <AddPackPicker availablePacks={availablePacks} onAdd={addPack} />

                {/* The replacement flow's own remarks section, not a second field
                    styled to match it: same heading weight, same counter, same error
                    treatment. showAcknowledgement is off because a correction has no
                    approval to acknowledge. */}
                <Box ref={(node) => registerField("remark", node)}>
                <RemarksSection
                    testId="edit-pack-remark"
                    label={
                        isMulti
                            ? "Reason for these corrections"
                            : "Reason for this correction"
                    }
                    placeholder="Explain what was wrong and why it is being corrected"
                    maxLength={MAX_PACK_REMARK_LENGTH}
                    showAcknowledgement={false}
                    remarks={remark}
                    errors={{
                        remarks:
                            typeof errors.remark === "string"
                                ? errors.remark
                                : errors.remark
                                ? "Please explain why these details are being corrected"
                                : false,
                    }}
                    onRemarksChange={(value) => {
                        setRemark(value);
                        setErrors((prev) => ({
                            ...prev,
                            remark: undefined,
                            remarkCount: undefined,
                            submit: undefined,
                        }));
                    }}
                />
                </Box>

                {/* Only ever holds what points at no field -- "nothing to save yet",
                    or a message from the server. Everything a user can fix in place is
                    reported on the field itself, which the page scrolls to and focuses,
                    because a summary down here can sit off screen from every card it
                    is about. */}
                {errors.submit && (
                    <NoticeBanner
                        testId="edit-pack-submit-error"
                        title="This correction cannot be saved yet"
                    >
                        {errors.submit}
                    </NoticeBanner>
                )}

                {/* mt only, no rule: Record Replacement's footer sits 8px under its
                    remarks section with nothing drawn between them, and this page is
                    meant to read as the same surface. */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        mt: "8px",
                    }}
                >
                    <CancelFormActionButton
                        data-testid="edit-pack-cancel"
                        disabled={submitting}
                        onClick={requestCancel}
                    >
                        Cancel
                    </CancelFormActionButton>
                    <PrimaryFormLoadingActionButton
                        data-testid="edit-pack-confirm"
                        disabled={submitting}
                        onClick={handleSubmit}
                        loading={submitting}
                    >
                        Confirm Change
                    </PrimaryFormLoadingActionButton>
                </Box>
            </Box>

            <Dialog
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                PaperProps={{ sx: { borderRadius: "16px", maxWidth: "460px", p: 3 } }}
            >
                <Typography
                    sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
                >
                    Discard this correction?
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#6B6B6B", mb: 3 }}>
                    The details you entered and your reason for the correction
                    won&apos;t be saved.
                </Typography>
                {/* mt only, no rule: Record Replacement's footer sits 8px under its
                    remarks section with nothing drawn between them, and this page is
                    meant to read as the same surface. */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        mt: "8px",
                    }}
                >
                    <CancelFormActionButton
                        data-testid="edit-pack-cancel-keep"
                        onClick={() => setCancelOpen(false)}
                    >
                        Keep editing
                    </CancelFormActionButton>
                    <PrimaryFormLoadingActionButton
                        data-testid="edit-pack-cancel-confirm"
                        onClick={closeTab}
                    >
                        Discard
                    </PrimaryFormLoadingActionButton>
                </Box>
            </Dialog>
        </Box>
        </ScreenContainer>
    );
}

// "Add another pack to this correction" — the edit counterpart of the replacement
// flow's AddPackMenu, so a user who realises a fourth pack is wrong does not have to
// abandon the page and re-select from the grid.
//
// Lists only packs not already in the correction, each row showing its slot and the
// serial currently on record, which is what a user matches against a sticker.


function AddPackPicker({ availablePacks = [], onAdd = () => {} }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const isEmpty = !availablePacks.length;

    const trigger = (
        <Box
            data-testid="add-pack-menu"
            onClick={isEmpty ? undefined : (event) => setAnchorEl(event.currentTarget)}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: `1px dashed ${isEmpty ? "#E0E0E0" : "#B9BBF8"}`,
                borderRadius: "10px",
                padding: "16px",
                mb: 2,
                cursor: isEmpty ? "default" : "pointer",
                color: isEmpty ? "#B0B0B0" : "#4A4AF4",
                backgroundColor: isEmpty ? "#FAFAFA" : "transparent",
                transition: "background-color .15s, border-color .15s",
                ...(!isEmpty && {
                    "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6" },
                }),
            }}
        >
            <AddIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Add another pack to this correction
            </Typography>
        </Box>
    );

    return (
        <>
            {isEmpty ? (
                <PrimaryTooltip
                    title="Every pack on this bus is already in this correction"
                    placement="top"
                >
                    {trigger}
                </PrimaryTooltip>
            ) : (
                trigger
            )}

            <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{
                    paper: {
                        sx: {
                            // Match the trigger's width so the list reads as an
                            // extension of it rather than a floating popover.
                            width: anchorEl?.offsetWidth,
                            maxHeight: "320px",
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(2, 6, 24, 0.12)",
                            border: "1px solid #EAEAEA",
                        },
                    },
                }}
                MenuListProps={{ sx: { py: 0 } }}
            >
                {availablePacks.map((pack) => (
                    <MenuItem
                        key={pack.absPackNo}
                        data-testid={`add-pack-${pack.absPackNo}`}
                        onClick={() => {
                            setAnchorEl(null);
                            onAdd(pack);
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px",
                            padding: "14px 20px",
                            borderBottom: "1px solid #F0F0F0",
                            "&:last-of-type": { borderBottom: 0 },
                            "&:hover": { backgroundColor: "#F7F7FF" },
                        }}
                    >
                        <Typography
                            sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}
                        >
                            {[pack.stringLabel, pack.label].filter(Boolean).join(" · ")}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: "#9A9A9A",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {pack.serial || "No serial on record"}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}


// One banner for everything the page needs to say about the form as a whole.
//
// Replaces a bare line of red text under the cards, which read as unfinished and --
// worse -- sat too far from the fields it described to be actionable. With several
// cards open it could be off screen from both packs it was talking about.
function NoticeBanner({ tone = "error", title, children, testId }) {
    // Every value here is one the replacement flow already uses, so the two screens
    // share one palette rather than two that merely look alike:
    //   #B54708 / #F5E5B8 / #FDF6E3 are its warning text, border and ground
    //   #F04438 / #E5484D are its error field-border and error text
    // The error GROUND is #FEF3F2 -- the replacement flow has no banner of its own to
    // borrow one from, so this is the value the rest of the repo already pairs with
    // #F04438 (DocumentUpload's failed row). Deliberately NOT the warning cream: a
    // blocking error and an advisory must not read as the same kind of message.
    const palette =
        tone === "warning"
            ? { border: "#F5E5B8", bg: "#FDF6E3", icon: "#B54708", text: "#B54708" }
            : { border: "#F04438", bg: "#FEF3F2", icon: "#F04438", text: "#E5484D" };

    return (
        <Box
            data-testid={testId}
            sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                border: `1px solid ${palette.border}`,
                backgroundColor: palette.bg,
                borderRadius: "10px",
                padding: "14px 16px",
                mb: 2,
            }}
        >
            {tone === "warning" ? (
                <WarningAmberOutlinedIcon
                    sx={{ fontSize: 20, color: palette.icon, mt: "1px" }}
                />
            ) : (
                <ErrorOutlineIcon sx={{ fontSize: 20, color: palette.icon, mt: "1px" }} />
            )}
            <Box>
                {title && (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: palette.text,
                            mb: 0.5,
                        }}
                    >
                        {title}
                    </Typography>
                )}
                <Typography
                    component="div"
                    sx={{ fontSize: "13px", color: palette.text, lineHeight: 1.6 }}
                >
                    {children}
                </Typography>
            </Box>
        </Box>
    );
}


// Shown when any selected pack already has a replacement on record.
//
// Deliberately a warning, not a block: correcting a serial that was mistyped while
// recording a replacement is a legitimate and expected use of this screen. What the
// user cannot see from here is that the replacement log keeps its own frozen copy of
// that serial, so after this correction the pack card and Replacement Logs will
// disagree -- and nothing on either screen explains why. That is the thing worth
// saying before they submit, not after.
//
// The chrome is NoticeBanner's, not a second copy of it: this banner and the warning
// tone were pixel-identical down to the icon offset, which is exactly how two
// "identical" surfaces drift apart on the next palette change.
function ReplacementWarning({ warning, isMulti }) {
    const { count, dates } = warning;
    return (
        <NoticeBanner
            tone="warning"
            testId="edit-pack-replacement-warning"
            title={
                count === 1
                    ? "This pack has already been replaced"
                    : `${count} of these packs have already been replaced`
            }
        >
            A replacement was recorded through the Replace flow, which is the normal way
            a pack's serial changes. Only continue if the details below were entered
            wrongly at the time.
            <Box sx={{ mt: 0.75 }}>
                Correcting {isMulti ? "them" : "it"} here does <b>not</b> update the
                replacement record. Replacement Logs will keep showing the serial as it
                was recorded, so the two screens will disagree.
            </Box>
            {dates.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {dates.map(({ name, lastReplacedAt }) => (
                        <Box key={name} sx={{ fontSize: "12px" }}>
                            {name}
                            {lastReplacedAt ? ` \u2014 replaced ${lastReplacedAt}` : ""}
                        </Box>
                    ))}
                </Box>
            )}
        </NoticeBanner>
    );
}

function BackLink({ onClick }) {
    return (
        <Box
            data-testid="edit-pack-back"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick();
                }
            }}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                color: "#6B6B6B",
                fontSize: "13px",
                mb: "8px",
                "&:hover": { color: "#6D72F6" },
            }}
        >
            <KeyboardArrowLeftIcon sx={{ fontSize: "20px" }} />
            Back
        </Box>
    );
}

// One pack's card: the values on record, then the three that replace them.
//
// The three inputs share a wrapping row. flexWrap responds to the CARD's width rather
// than the viewport, so the same rule holds in a narrow page shell as on a small
// screen. The serial gets a wider basis because it is a 29-character monospace value,
// and minWidth:0 lets a flex item shrink below its content instead of forcing the row
// to overflow the card.
//
// The current-values grid above them does NOT share those tracks -- it is equal thirds
// on viewport breakpoints. The two rows read as a pair without being column-aligned,
// which is deliberate: aligning them would mean giving the read-only summary the same
// px bases as the inputs, and a serial is far longer as an input value than as a label.
function PackEditCard({
    pack,
    form,
    errors,
    changed,
    batteryID,
    canRemove = false,
    onRemove = () => {},
    onChange,
    registerField = () => {},
    editedAbsPackNos = [],
}) {
    // Suggestions come from the same endpoint and the same "incoming" mode the
    // replacement flow's New-pack-serial field uses, so the two screens offer the
    // identical list rather than two definitions of "available".
    const suggestionsHandler = useCallback(
        (search, columnName, limit) =>
            getPackSerialSuggestions(batteryID, { mode: "incoming", search, limit }),
        [batteryID]
    );

    // What the registry lookup found for a given serial, keyed by the term it ran for
    // so the advisory does not flash while a newer lookup is in flight.
    //
    // Deliberately NOT inferred from the suggestion list. Suggestions now offer only
    // FREE serials, so "absent from suggestions" covers two opposite situations -- a
    // serial nobody has heard of, and one that is taken -- and calling both "unmatched,
    // please verify" tells the user to check the one thing that is not the problem.
    // Only the registry can tell them apart.
    const [serialLookup, setSerialLookup] = useState(null);
    const [autoFilledDateSerial, setAutoFilledDateSerial] = useState(null);

    const isMounted = useRef(true);
    const pendingLookup = useRef(null);
    const lookupTimer = useRef(null);
    useEffect(
        () => () => {
            isMounted.current = false;
            if (lookupTimer.current) clearTimeout(lookupTimer.current);
        },
        []
    );

    // Picking a registered serial fills the manufacturing date from the registry.
    // Second call by design: the suggestions endpoint returns a flat string array so it
    // stays shared with the inventory table search.
    const applySerialDetail = useCallback(
        async (serial) => {
            // Already asked, or asking, about this exact serial. Picking a suggestion
            // both calls this directly AND changes the search box's value, which
            // re-fires its suggestions effect -- without this that one click cost two
            // identical registry reads.
            if (pendingLookup.current === serial) return;
            pendingLookup.current = serial;
            const res = await getBatteryPackBySerial(serial);
            if (!isMounted.current || pendingLookup.current !== serial) return;
            // A failed request is not evidence of anything. Recording "not found" here
            // would tell the user their serial is unknown whenever the endpoint is
            // down, which is the same lie in the other direction.
            if (res?.responseStatus?.code !== 200) return;
            const rows = res.response?.data || [];
            const row = rows[0] || {};
            setSerialLookup({ term: serial, found: rows.length > 0 });

            // The RAW row, not a verdict computed from it. Whether this serial clashes
            // depends on which slots the request covers, and that changes every time
            // the user adds or removes a pack -- so the verdict is derived at render
            // and at validate time, where the current pack set is in scope, and the
            // lookup only stores the evidence.
            onChange("serialRegistry")({ term: serial, row });

            const registryDate = toCalendarDay(row.manufacturingDate);
            if (registryDate) {
                onChange("manufacturingDate")(registryDate);
                setAutoFilledDateSerial(serial);
            }
        },
        // Neither batteryID nor editedAbsPackNos any more: this only stores the raw
        // registry row, and whether that row is a conflict is decided at render and
        // validate time. Keeping editedAbsPackNos here would rebuild the callback on
        // every add and remove for nothing.
        [onChange]
    );

    const trackSuggestions = (suggestions, searchTerm) => {
        // Runs whether or not the term appeared in the list. Gating this on a match was
        // safe only while suggestions included taken serials; now that they do not, the
        // UNLISTED case is precisely the one worth a lookup -- it is where both the
        // registry date and any conflict live.
        //
        // Debounced here, in the caller. DeviceSearchInput has no debounce of its own
        // -- its effect runs on every distinct searchTerm -- so removing the `matched`
        // gate would otherwise have added one registry read per keystroke on top of the
        // suggestions read the box already makes. A 29-character serial meant ~57
        // requests for one field.
        if (lookupTimer.current) clearTimeout(lookupTimer.current);
        if (
            (searchTerm || "").length >= MIN_SERIAL_SEARCH_LENGTH &&
            searchTerm !== autoFilledDateSerial
        ) {
            lookupTimer.current = setTimeout(
                () => applySerialDetail(searchTerm),
                SERIAL_LOOKUP_DEBOUNCE_MS
            );
        }
    };

    // The registry date is bound to the exact serial it was fetched for. Edit the serial
    // and that date is no longer an answer about anything, so it is cleared along with
    // the lock and the caption -- otherwise the field silently keeps the PREVIOUS
    // serial's manufacturing date, unlocked, uncaptioned, and submits it. Same
    // release the Replace flow performs, through the same matcher so the lock and the
    // release can never disagree about what counts as the same serial.
    const releaseAutoFilledDate = (nextSerial) => {
        if (!autoFilledDateSerial) return;
        if (matchesAutoFilledSerial(autoFilledDateSerial, nextSerial)) return;
        pendingLookup.current = null;
        setAutoFilledDateSerial(null);
        onChange("manufacturingDate")(null);
    };

    const handleSerialChange = (next) => {
        onChange("customerPackID")(next);
        onChange("serialRegistry")(null);
        setSerialLookup(null);
        // Unconditionally, NOT inside releaseAutoFilledDate -- that early-returns when
        // no date was auto-filled, which is every serial whose registry row carries no
        // manufacturing date and every serial whose first lookup has not landed yet.
        // An in-flight read for the old serial would then pass its own guard and write
        // a conflict for a serial the user no longer has typed.
        pendingLookup.current = null;
        releaseAutoFilledDate(next);
    };

    const handleSerialSelect = (serial) => {
        onChange("customerPackID")(serial);
        applySerialDetail(serial);
    };

    const trimmedSerial = (form?.customerPackID || "").trim();

    // Re-derived on every render from the CURRENT request. The registry answer is a
    // fact about the serial; whether it is a clash is a fact about this request.
    const serialRegistry = form?.serialRegistry;
    const serialConflict =
        serialRegistry && serialRegistry.term === trimmedSerial
            ? getSerialSlotConflict(serialRegistry.row, {
                  batteryID,
                  editedAbsPackNos,
              })
            : null;

    // Genuinely unknown: the registry was asked about this exact serial and had no row
    // for it. A serial that IS known but taken is not unmatched -- it gets the conflict
    // message naming the pack that holds it, which is the actionable one.
    const isUnmatched =
        trimmedSerial.length >= MIN_SERIAL_SEARCH_LENGTH &&
        !!serialLookup &&
        serialLookup.term === trimmedSerial &&
        !serialLookup.found;

    // The shared ladder, not a second copy of it: an error outranks the unmatched
    // advisory, because a hand-typed serial is unmatched by definition and letting the
    // advisory win would hide the real reason the form will not submit. `fallback` is
    // where this screen's own "was <serial>" hint goes.
    const serialMessage = getSerialFieldMessage({
        // The conflict shows the moment the lookup answers, not only once Confirm has
        // been pressed: the point is to stop the user filling the rest of the form.
        error:
            errors.customerPackID || formatSerialSlotConflict(serialConflict) || null,
        isUnmatched,
        fallback:
            changed && pack?.serial && trimmedSerial !== pack.serial
                ? `was ${pack.serial}`
                : " ",
    });

    const isDateLocked = isRegistryDateLocked(
        autoFilledDateSerial,
        trimmedSerial,
        form?.manufacturingDate
    );
    const chipLabel = [pack?.stringLabel, pack?.label].filter(Boolean).join(" · ");

    return (
        <Box data-testid={`edit-pack-card-${pack?.absPackNo}`}>
        <PackCardShell
            chipLabel={chipLabel}
            chipTestId={`pack-chip-${pack?.absPackNo}`}
            headerExtra={changed ? <ChangedChip /> : null}
            actions={
                canRemove && (
                    <PrimaryTooltip
                        title="Remove this pack from the correction"
                        placement="top"
                    >
                        <IconButton
                            size="small"
                            data-testid={`remove-pack-${pack?.absPackNo}`}
                            onClick={() => onRemove(pack)}
                            sx={{ color: "#8D8D8D", "&:hover": { color: "#E5484D" } }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </PrimaryTooltip>
                )
            }
        >
            <>
                {/* ---- OUTGOING: what is on record now ---- */}
                <Box
                    sx={{
                        ...OUTGOING_BAND_SX,
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.7fr)",
                        },
                    }}
                >
                    <BandLabel text="OUTGOING" color={OUTGOING_BAND_COLOR} />

                    <ReadOnlyValue
                        label="Current pack serial number"
                        value={pack?.serial}
                        testId={`current-serial-${pack?.absPackNo}`}
                    />
                    <ReadOnlyValue
                        label="Manufacturing date"
                        value={
                            pack?.manufacturingDate
                                ? DateFormatString(pack.manufacturingDate)
                                : null
                        }
                    />
                    <ReadOnlyValue
                        label="Installed on"
                        value={
                            pack?.installedOn
                                ? DateFormatString(
                                      getLocalDateFromUTC(pack.installedOn, "YYYY-MM-DD")
                                  )
                                : null
                        }
                    />
                </Box>

                {/* ---- Arrow, straddling the two bands ---- */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        // Pull the two bands together and float the arrow over the seam.
                        my: "-14px",
                        position: "relative",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CircleArrowRightIcon direction="down" size={34} />
                </Box>

                {/* ---- INCOMING: what replaces it ---- */}
                <Box
                    sx={{
                        ...INCOMING_BAND_SX,
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.7fr)",
                        },
                    }}
                >
                    <BandLabel text="INCOMING" color={INCOMING_BAND_COLOR} />

                    <Box
                        sx={{ pt: "14px" }}
                        ref={(node) =>
                            registerField(`${pack?.absPackNo}:customerPackID`, node)
                        }
                    >
                        <FieldLabel required>New pack serial number</FieldLabel>
                        <SerialSearchField
                            value={form?.customerPackID || ""}
                            onValueChange={handleSerialChange}
                            onSelect={handleSerialSelect}
                            searchSuggestionsHandler={suggestionsHandler}
                            onSuggestionsChange={trackSuggestions}
                            error={serialMessage.isError ? serialMessage.text : false}
                            isWarning={serialMessage.isWarning}
                            helperText={serialMessage.text}
                            placeholder="Search or type the serial number"
                            testId={`edit-pack-serial-${pack?.absPackNo}`}
                        />
                    </Box>

                    <Box
                        sx={{ pt: "14px" }}
                        ref={(node) =>
                            registerField(`${pack?.absPackNo}:manufacturingDate`, node)
                        }
                    >
                        <FieldLabel>MFG date</FieldLabel>
                        <SingleDatePicker
                            dataTestId={`edit-pack-mfg-${pack?.absPackNo}`}
                            value={form?.manufacturingDate || null}
                            handleOnChange={(date) =>
                                onChange("manufacturingDate")(
                                    date ? date.format("YYYY-MM-DD") : null
                                )
                            }
                            // Org's today, not the browser's: a support user in another
                            // zone would otherwise have the org's real today greyed out.
                            maxDate={getDatePickerDate()}
                            placeholder="Select date"
                            format="YYYY-MM-DD"
                            singleIcon
                            fullWidth
                            // Read-only while it holds the registry's answer for this
                            // serial -- editing it would silently disagree with the
                            // record.
                            disabled={isDateLocked}
                            // No local rule can fail here today, but a server conflict
                            // can name this field -- someone else moving the date is
                            // exactly what the `expected` guard raises on. Without a
                            // visible state the page would scroll to a field showing
                            // nothing wrong.
                            status={errors.manufacturingDate ? "error" : ""}
                        />
                        <Typography
                            sx={{
                                fontSize: "12px",
                                mt: "5px",
                                minHeight: "16px",
                                color: errors.manufacturingDate ? "#E5484D" : "#6B6B6B",
                            }}
                        >
                            {errors.manufacturingDate ||
                                (isDateLocked
                                    ? "Auto-filled from the pack registry"
                                    : " ")}
                        </Typography>
                    </Box>

                    <Box
                        sx={{ pt: "14px" }}
                        ref={(node) =>
                            registerField(`${pack?.absPackNo}:installedOn`, node)
                        }
                    >
                        <FieldLabel>Installed on</FieldLabel>
                        <SingleDatePicker
                            dataTestId={`edit-pack-installed-${pack?.absPackNo}`}
                            value={form?.installedOn || null}
                            handleOnChange={(date) =>
                                onChange("installedOn")(
                                    date ? date.format("YYYY-MM-DD") : null
                                )
                            }
                            maxDate={getDatePickerDate()}
                            placeholder="Select date"
                            format="YYYY-MM-DD"
                            singleIcon
                            fullWidth
                            status={errors.installedOn ? "error" : ""}
                        />
                        <Typography
                            sx={{
                                fontSize: "12px",
                                mt: "5px",
                                minHeight: "16px",
                                color: errors.installedOn ? "#E5484D" : "#6B6B6B",
                            }}
                        >
                            {errors.installedOn || " "}
                        </Typography>
                    </Box>
                </Box>
            </>
        </PackCardShell>
        </Box>
    );
}


// A value on record, laid out to sit on the same baseline as the inputs opposite it.
function ReadOnlyValue({ label, value, testId }) {
    return (
        <Box sx={{ pt: "14px" }}>
            <FieldLabel>{label}</FieldLabel>
            <Typography
                data-testid={testId}
                sx={{
                    fontSize: "14px",
                    color: value ? "#1A1A1A" : "#9A9A9A",
                    lineHeight: "40px",
                    overflowWrap: "anywhere",
                }}
            >
                {value || "N/A"}
            </Typography>
        </Box>
    );
}


